/**
 * Last line of defence for links that leave React Router: plain anchors with
 * `target="_blank"` (shipment cards, print links, report drill-downs).
 *
 * When the stored token has already expired, the click is cancelled and this
 * tab goes to the login screen — so the protected page is never opened in a
 * new tab just to be kicked back to login a moment later.
 *
 * It only acts when a token exists *and* has expired, so login/signup pages
 * and any public screen behave exactly as before.
 */
import {
    ensureActiveSession,
    getAuthToken,
    isPublicPath,
    isTokenExpired,
} from "@/utility/authSession";

const sessionAlreadyExpired = () => {
    const token = getAuthToken();
    return Boolean(token) && isTokenExpired(token);
};

export const installProtectedLinkGuard = () => {
    if (typeof document === "undefined") return () => {};

    const onClick = (event) => {
        if (event.defaultPrevented || (event.button !== 0 && event.button !== 1)) return;

        const anchor = event.target?.closest?.("a[href]");
        if (!anchor) return;

        const raw = anchor.getAttribute("href") || "";
        if (!raw || raw.startsWith("#") || /^(mailto|tel|javascript|blob|data):/i.test(raw)) return;

        let url;
        try {
            url = new URL(anchor.href, window.location.origin);
        } catch {
            return;
        }
        if (url.origin !== window.location.origin) return; // external link
        if (isPublicPath(url.pathname)) return;

        if (!sessionAlreadyExpired()) return;

        event.preventDefault();
        event.stopPropagation();
        ensureActiveSession();
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
        document.removeEventListener("click", onClick, true);
        document.removeEventListener("auxclick", onClick, true);
    };
};
